import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, HandHeart, Building2, Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { CONTACT_EMAIL, CONTACT_LOCATION } from "@/constants/contact";

const PATHWAYS = [
  { Icon: GraduationCap, title: "I'm a student (or family)", text: "Curious about IBY programs at your school, or want to join? Start here.", type: "Student/Family" },
  { Icon: HandHeart, title: "I want to mentor", text: "One hour a week. Full training and matching provided. Change a trajectory.", type: "Mentor" },
  { Icon: Building2, title: "I want to partner", text: "Fund programs, host placements, or lend your people as mentors.", type: "Partner" },
];

const INQUIRY_TYPES = ["Mentor", "Partner", "School", "Student/Family", "General"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", org: "", type: "General", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      return toast.error("Please fill in your name, a valid email, and a message.");
    }

    setSubmitting(true);
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${CONTACT_EMAIL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || "Not provided",
          organisation: form.org || "Not provided",
          inquiry_type: form.type,
          message: form.message,
          _subject: `IBY Contact: ${form.type} — ${form.name}`,
          _template: "table",
          _captcha: "false",
        }),
      });

      if (!response.ok) throw new Error("Failed to send message");

      toast.success(`Thanks ${form.name.split(" ")[0]}! We'll be in touch within two business days.`);
      setForm({ name: "", email: "", phone: "", org: "", type: "General", message: "" });
    } catch {
      toast.error(`Something went wrong. Please try again or email us at ${CONTACT_EMAIL}.`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-[#0B132B] text-white pt-40 pb-24 grain relative overflow-hidden" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
          <Reveal>
            <Overline light>Contact</Overline>
            <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
              Let's talk. Whoever you are, there's a door here for you.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20" data-testid="contact-pathways">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PATHWAYS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <button
                onClick={() => {
                  setForm((f) => ({ ...f, type: p.type }));
                  document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`pathway-card-${i}`}
                className="group text-left bg-white border border-black/5 rounded-[2rem] p-10 w-full h-full hover:border-[#FF5A36]/40 hover:-translate-y-2 transition-[transform,border-color] duration-500"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#FF5A36]/10 flex items-center justify-center mb-8 group-hover:bg-[#FF5A36] transition-colors duration-500">
                  <p.Icon size={26} className="text-[#FF5A36] group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="font-display font-semibold text-2xl tracking-tight text-[#0B132B] mb-3">{p.title}</h3>
                <p className="text-[#4A5568] leading-relaxed text-sm mb-6">{p.text}</p>
                <span className="inline-flex items-center gap-2 font-bold text-[#FF5A36] text-sm group-hover:gap-4 transition-all duration-300">
                  Get in touch <ArrowRight size={15} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="contact-form" className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <Overline>Details</Overline>
              <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-[#0B132B] mb-8">
                We reply to every message.
              </h2>
              <div className="space-y-5 text-[#374151]">
                <p className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
                    <Mail size={17} className="text-[#FF5A36]" />
                  </span>
                  <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-[#FF5A36] transition-colors duration-300">
                    {CONTACT_EMAIL}
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
                    <Phone size={17} className="text-[#FF5A36]" />
                  </span>
                  [Phone number]
                </p>
                <p className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-[#FF5A36]/10 flex items-center justify-center shrink-0">
                    <MapPin size={17} className="text-[#FF5A36]" />
                  </span>
                  {CONTACT_LOCATION}
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="bg-white border border-black/5 rounded-[2rem] p-8 md:p-12" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-[#0B132B] mb-2" htmlFor="c-name">Name *</label>
                    <input id="c-name" value={form.name} onChange={set("name")} data-testid="contact-name-input" placeholder="Your full name"
                      className="w-full rounded-xl border border-black/10 bg-[#FAF8F5] px-5 py-3.5 focus:outline-none focus:border-[#FF5A36] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#0B132B] mb-2" htmlFor="c-email">Email *</label>
                    <input id="c-email" type="email" value={form.email} onChange={set("email")} data-testid="contact-email-input" placeholder="you@example.com"
                      className="w-full rounded-xl border border-black/10 bg-[#FAF8F5] px-5 py-3.5 focus:outline-none focus:border-[#FF5A36] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#0B132B] mb-2" htmlFor="c-phone">Phone</label>
                    <input id="c-phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" placeholder="Optional"
                      className="w-full rounded-xl border border-black/10 bg-[#FAF8F5] px-5 py-3.5 focus:outline-none focus:border-[#FF5A36] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#0B132B] mb-2" htmlFor="c-org">Organisation</label>
                    <input id="c-org" value={form.org} onChange={set("org")} data-testid="contact-org-input" placeholder="Optional"
                      className="w-full rounded-xl border border-black/10 bg-[#FAF8F5] px-5 py-3.5 focus:outline-none focus:border-[#FF5A36] transition-colors" />
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-bold text-[#0B132B] mb-3">I'm reaching out as a…</label>
                  <div className="flex flex-wrap gap-2" data-testid="inquiry-type-group">
                    {INQUIRY_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm({ ...form, type: t })}
                        data-testid={`inquiry-type-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-300 ${
                          form.type === t ? "bg-[#FF5A36] text-white" : "bg-[#FAF8F5] border border-black/10 text-[#4A5568] hover:border-[#FF5A36]/50"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <label className="block text-sm font-bold text-[#0B132B] mb-2" htmlFor="c-message">Message *</label>
                  <textarea id="c-message" rows={5} value={form.message} onChange={set("message")} data-testid="contact-message-input" placeholder="Tell us a little about yourself and what you're looking for…"
                    className="w-full rounded-xl border border-black/10 bg-[#FAF8F5] px-5 py-3.5 focus:outline-none focus:border-[#FF5A36] transition-colors resize-none" />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="mt-8 rounded-full bg-[#FF5A36] text-white px-10 py-4 font-bold hover:bg-[#E04825] transition-colors duration-300 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Message"} <ArrowRight size={18} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
